import React, { useEffect, useState } from 'react';
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import api from '../../../services/api';

const filter = createFilterOptions();
const CREATE_MARKER = '__create__';

// Autocomplete multi-valor pra marcar/desmarcar tags (`ConversationList`) de
// uma conversa - digitando, mostra as tags já cadastradas na conta que
// batem com o texto (via `options`); se nenhuma bater, aparece uma opção
// "+ Criar tag "..."" (via `filterOptions`/`CREATE_MARKER`) que cria a tag
// na hora e já marca a conversa. `freeSolo` também deixa apertar Enter
// direto num texto novo, sem precisar clicar na opção.
const ConversationTagsInput = ({ appId, conversationId }) => {
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!appId) return;
        api.get(`v1/apps/${appId}/conversation_lists/`)
            .then((r) => setAvailableTags(r.data || []))
            .catch((error) => console.error('Erro ao carregar tags da conta', error));
    }, [appId]);

    useEffect(() => {
        if (!appId || !conversationId) return;
        api.get(`v1/apps/${appId}/conversations/${conversationId}/`)
            .then((r) => setSelectedTags(r.data?.conversation?.tags || []))
            .catch((error) => console.error('Erro ao carregar tags da conversa', error));
    }, [appId, conversationId]);

    const handleChange = async (event, newValue) => {
        const newNames = newValue
            .map((v) => {
                const raw = typeof v === 'string' ? v : v.inputValue || v.name || '';
                return raw.startsWith(CREATE_MARKER) ? raw.slice(CREATE_MARKER.length) : raw;
            })
            .map((s) => s.trim())
            .filter(Boolean);

        const previousNames = selectedTags;
        const added = newNames.filter((n) => !previousNames.some((p) => p.toLowerCase() === n.toLowerCase()));
        const removed = previousNames.filter((p) => !newNames.some((n) => n.toLowerCase() === p.toLowerCase()));

        if (added.length === 0 && removed.length === 0) return;

        setSelectedTags(newNames);
        setLoading(true);
        try {
            for (const name of added) {
                let tag = availableTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
                if (!tag) {
                    const res = await api.post(`v1/apps/${appId}/conversation_lists/`, { name });
                    tag = res.data;
                    setAvailableTags((prev) => [...prev, tag]);
                }
                await api.post(`v1/apps/${appId}/conversation_lists/${tag.id}/conversations/${conversationId}/`);
            }
            for (const name of removed) {
                const tag = availableTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
                if (tag) {
                    await api.delete(`v1/apps/${appId}/conversation_lists/${tag.id}/conversations/${conversationId}/`);
                }
            }
        } catch (error) {
            console.error('Erro ao atualizar tags da conversa', error);
            setSelectedTags(previousNames);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            size="small"
            options={availableTags.map((t) => t.name)}
            value={selectedTags}
            onChange={handleChange}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const inputValue = params.inputValue.trim();
                const jaExiste = options.some((option) => option.toLowerCase() === inputValue.toLowerCase());
                if (inputValue !== '' && !jaExiste) {
                    filtered.push(`${CREATE_MARKER}${inputValue}`);
                }
                return filtered;
            }}
            getOptionLabel={(option) => (option.startsWith(CREATE_MARKER) ? `+ Criar tag "${option.slice(CREATE_MARKER.length)}"` : option)}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} />)
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Tags"
                    placeholder={selectedTags.length === 0 ? 'Adicionar tag...' : ''}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={14} />}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
};

export default ConversationTagsInput;
